import {
  useCallback, useLayoutEffect, useRef, useState,
} from 'react';
import { useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material';

/**
 * Checks whether or not the current device is a mobile device, based on device width
 */
export const useIsMobile = () => useMediaQuery<Theme>((theme) => theme.breakpoints.down('md'), { noSsr: true });

export const useHasHover = () => useMediaQuery<Theme>('@media (hover: hover)', { noSsr: true });

export const useHasScrolled = (): [React.RefObject<any>, boolean] => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const observer = useRef(null);
  const sentinel = useRef(null);

  const onSentinelIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      setIsIntersecting(entry.isIntersecting);
    });
  }, []);

  useLayoutEffect(() => {
    // create IntersectionObserver
    observer.current = new IntersectionObserver(onSentinelIntersection);

    // Tell the observer to observer sentinel.current
    if (sentinel.current) {
      observer.current.observe(sentinel.current);
      sentinel.current.className = 'pixel-to-watch';
    }

    return () => observer.current && observer.current.disconnect();
  }, [onSentinelIntersection]);

  return [sentinel, !isIntersecting];
};

/**
 * Send user to bottom of a list on mount
 * @param {string} nodeRef
 */
export const useScrollToBottom = (nodeRef: React.RefObject<any>) => {
  const ref = nodeRef;

  const mutationObserver = useRef(null);
  const [follow, setFollow] = useState(true);

  const handleScroll = useCallback(() => {
    if (ref.current) {
      const offsetFromBottom = (ref.current.scrollHeight - ref.current.offsetHeight) - ref.current.scrollTop;

      if (offsetFromBottom <= 0) {
        setFollow(true);
      } else if (offsetFromBottom > 0) {
        setFollow(false);
      }
    }
  }, [ref, setFollow]);

  const scrollToBottomCallback = useCallback(() => {
    if (ref.current) {
      const offsetFromBottom = (ref.current.scrollHeight - ref.current.offsetHeight) - ref.current.scrollTop;

      const lastChild = ref.current.children && ref.current.children[ref.current.children.length - 1];
      const lastMessageHeight = lastChild?.offsetHeight || 0;

      const bufferSpace = offsetFromBottom - lastMessageHeight;

      if (follow || bufferSpace <= lastMessageHeight) {
        ref.current.scrollTop = ref.current.scrollHeight;
      }
    }
  }, [follow, ref]);

  useLayoutEffect(() => {
    if (ref.current) {
      const { current } = ref;

      mutationObserver.current = new MutationObserver(scrollToBottomCallback);
      mutationObserver.current.observe(current, { attributes: true, childList: true, subtree: true });
      current.addEventListener('scroll', handleScroll);

      return () => mutationObserver.current && mutationObserver.current.disconnect() && current.removeEventListener('scroll', handleScroll);
    }

    return undefined;
  }, [follow, ref, handleScroll, scrollToBottomCallback]);
};
